import { ExpandMore as ExpandMoreIcon } from 'components/icons';
import BasicTable from 'components/tables/BasicTable';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import React, { useMemo } from 'react';
import { Classifier, ClassifierText, EntityClassifierValue } from 'types/classifiers';
import { formatClassifierParameters } from 'utils/classifierUtil';
import { t } from 'utils/getTranslations';

interface ClassifierItemProps {
    classifier: Classifier;
}

const ClassifierItem: React.FC<ClassifierItemProps> = ({ classifier }) => {
    const columns = useMemo<DataTableColumnDef<EntityClassifierValue>[]>(
        () => [
            {
                header: t('classifiers.classifier.value.code'),
                accessorKey: 'code',
                cell: ({ getValue }) => getValue() ?? '',
            },
            {
                header: t('classifiers.classifier.value.text_eng'),
                accessorKey: 'texts',
                cell: ({ getValue }) => {
                    const englishText = getValue<ClassifierText[] | undefined>()?.find(
                        (text: ClassifierText) => text?.langCode?.toLocaleLowerCase() === 'eng'
                    );
                    return englishText?.value ?? '-';
                },
            },
            {
                header: t('classifiers.classifier.value.parameters'),
                accessorKey: 'parameters',
                cell: ({ getValue }) => formatClassifierParameters(getValue()) || '-',
            },
        ],
        []
    );

    return (
        <Collapsible className="border-divider bg-paper new:border-0 new:shadow-card new:outline-hidden overflow-hidden rounded-md border">
            <CollapsibleTrigger className="group bg-grey-100 flex min-h-[46px] w-full cursor-pointer items-center px-4 py-[9px] text-left">
                <span className="text-body1 grow font-semibold">
                    {classifier.code + (classifier.description ? ' — ' + classifier.description : '')}
                </span>
                <ExpandMoreIcon className="text-grey-500 transition-transform duration-150 group-data-[panel-open]:rotate-90" />
            </CollapsibleTrigger>
            <CollapsibleContent className="border-divider h-(--collapsible-panel-height) overflow-hidden border-t transition-[height] duration-150 ease-in-out data-ending-style:h-0 data-starting-style:h-0 motion-reduce:transition-none">
                {classifier.values && classifier.values.length > 0 ? (
                    <BasicTable data={classifier.values} striped={false} columns={columns} />
                ) : (
                    <p className="text-body1 p-4">{t('classifiers.classifier.values-not-found')}</p>
                )}
            </CollapsibleContent>
        </Collapsible>
    );
};

export default ClassifierItem;
